/**
 * Shared, dependency-free helpers for prebooking attachment uploads.
 * Used by both prebookService (single-PUT) and multipartUploadService (multipart)
 * so the S3 URL trust check and error shape stay in one place.
 */

/**
 * Custom error class that preserves the HTTP status code.
 */
export class AttachmentError extends Error {
  constructor(message, status, originalError) {
    super(message);
    this.name = 'AttachmentError';
    this.status = status;
    this.originalError = originalError;
  }
}

/**
 * Extract a status code from an error (direct `.status` or an axios-style `.response.status`).
 */
export const getErrorStatus = (error) => {
  return error?.status || error?.response?.status || null;
};

/**
 * Wrap an error as an AttachmentError with the status code preserved.
 */
export const createAttachmentError = (error, defaultMessage) => {
  const status = getErrorStatus(error);
  const message = error?.message || defaultMessage;
  return new AttachmentError(message, status, error);
};

const TRUSTED_S3_HOST_SUFFIXES = ['amazonaws.com'];
const TRUSTED_S3_HOSTS = ['localhost'];

/**
 * Guard: only allow uploads to HTTPS S3 (or localhost) hosts. Every presigned URL
 * must pass this before a PUT is issued.
 */
export const isTrustedS3Url = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const isHttps = parsed.protocol === 'https:' || host === 'localhost' || host === '127.0.0.1';
    const isWhitelisted = TRUSTED_S3_HOSTS.includes(host) ||
      TRUSTED_S3_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
    return isHttps && isWhitelisted;
  } catch (e) {
    return false;
  }
};
